import type { Tag, Track, ValidationError } from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Button, IconButton } from '../../components/Button.js'
import { Chip } from '../../components/Chip.js'
import { TextArea, TextField } from '../../components/Field.js'
import { useToast } from '../../components/Toast.js'
import { useDeclareUnsaved } from '../../pwa/UnsavedWork.js'
import { useServices } from '../../services/ServicesProvider.js'
import styles from './Templates.module.css'

/**
 * Building a check-in (T-303).
 *
 * Deliberately without conditional logic, branching, or required-versus-optional
 * rules. A template is a set of tracks in an order; every rule added here is a
 * decision the user has to make at 1pm with food going cold.
 */
export function TemplateEditorScreen() {
  const { repos } = useServices()
  const { show } = useToast()
  const navigate = useNavigate()
  const params = useParams()
  const templateId = params['id']
  const isNew = templateId === undefined || templateId === 'new'

  const [loaded, setLoaded] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [chosen, setChosen] = useState<string[]>([])
  const [tagIds, setTagIds] = useState<string[]>([])
  const [tracks, setTracks] = useState<Track[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [errors, setErrors] = useState<readonly ValidationError[]>([])
  const [dirty, setDirty] = useState(false)

  useDeclareUnsaved(dirty)

  const load = useCallback(async () => {
    const [allTracks, allTags] = await Promise.all([
      repos.tracks.list({ includeArchived: true }),
      repos.tags.list(),
    ])
    setTracks(allTracks)
    setTags(allTags)

    if (!isNew) {
      const template = await repos.templates.get(templateId)
      if (template) {
        setName(template.name)
        setDescription(template.description ?? '')
        setChosen([...template.trackIds])
        setTagIds([...template.defaultTagIds])
      }
    }
    setLoaded(true)
  }, [isNew, templateId, repos])

  useEffect(() => {
    void load()
  }, [load])

  const byId = new Map(tracks.map((track) => [track.id, track]))
  const available = tracks.filter(
    (track) => !track.archived && !chosen.includes(track.id),
  )

  function move(index: number, to: number) {
    if (to < 0 || to >= chosen.length) {
      return
    }
    const next = [...chosen]
    const [moved] = next.splice(index, 1)
    if (moved === undefined) {
      return
    }
    next.splice(to, 0, moved)
    setChosen(next)
    setDirty(true)
  }

  async function save() {
    setErrors([])
    const input = {
      name: name.trim(),
      trackIds: chosen,
      defaultTagIds: tagIds,
      ...(description.trim() === '' ? {} : { description: description.trim() }),
    }

    const result = isNew
      ? await repos.templates.create(input)
      : await repos.templates.update(templateId, input)

    if (!result.ok) {
      setErrors(result.errors)
      return
    }
    setDirty(false)
    show({ message: isNew ? `Created ${result.value.name}.` : 'Saved.' })
    void navigate('/templates')
  }

  if (!loaded) {
    return (
      <>
        <h1>Check-in</h1>
        <p>Loading…</p>
      </>
    )
  }

  return (
    <>
      <h1>{isNew ? 'New check-in' : name || 'Check-in'}</h1>

      <div className={styles['form']}>
        <section className={styles['section']}>
          <TextField
            label="Name"
            value={name}
            placeholder="Before eating, Bedtime…"
            onChange={(event) => {
              setName(event.target.value)
              setDirty(true)
            }}
          />
          <TextArea
            label="Description"
            optional
            value={description}
            onChange={(event) => {
              setDescription(event.target.value)
              setDirty(true)
            }}
          />
        </section>

        <section className={styles['section']}>
          <h2 className={styles['sectionTitle']}>
            Tracks, in the order you will see them
          </h2>

          {chosen.length === 0 ? (
            <p className={styles['empty']}>Nothing chosen yet.</p>
          ) : (
            <div className={styles['chosen']}>
              {chosen.map((trackId, index) => {
                const track = byId.get(trackId)
                // Archived or deleted since this template was written. Flagged
                // in place with a one-tap fix rather than a hard error — the
                // user should still be able to log the rest today.
                const stale = track === undefined || track.archived
                return (
                  <div
                    key={trackId}
                    className={`${styles['chosenTrack']} ${stale ? styles['stale'] : ''}`}
                  >
                    <span>
                      <span>{track?.name ?? 'A deleted track'}</span>
                      {stale && (
                        <span className={styles['staleNote']}>
                          {track === undefined
                            ? ' — this track no longer exists'
                            : ' — this track is archived, so it will not be asked for'}
                        </span>
                      )}
                    </span>
                    <span className={styles['actions']}>
                      <IconButton
                        label={`Move ${track?.name ?? 'track'} up`}
                        disabled={index === 0}
                        onClick={() => move(index, index - 1)}
                      >
                        <Chevron up />
                      </IconButton>
                      <IconButton
                        label={`Move ${track?.name ?? 'track'} down`}
                        disabled={index === chosen.length - 1}
                        onClick={() => move(index, index + 1)}
                      >
                        <Chevron />
                      </IconButton>
                      <IconButton
                        label={`Remove ${track?.name ?? 'this track'} from the check-in`}
                        onClick={() => {
                          setChosen(chosen.filter((id) => id !== trackId))
                          setDirty(true)
                        }}
                      >
                        <Cross />
                      </IconButton>
                    </span>
                  </div>
                )
              })}
            </div>
          )}

          {available.length > 0 && (
            <div className={styles['available']}>
              {available.map((track) => (
                <Chip
                  key={track.id}
                  onClick={() => {
                    setChosen([...chosen, track.id])
                    setDirty(true)
                  }}
                >
                  + {track.name}
                </Chip>
              ))}
            </div>
          )}
        </section>

        <section className={styles['section']}>
          <h2 className={styles['sectionTitle']}>Tags applied to every entry</h2>
          {tags.length === 0 ? (
            <p className={styles['empty']}>
              No tags yet. You can create them while logging.
            </p>
          ) : (
            <div className={styles['available']}>
              {tags.map((tag) => (
                <Chip
                  key={tag.id}
                  {...(tag.colour === undefined ? {} : { colour: tag.colour })}
                  selected={tagIds.includes(tag.id)}
                  onClick={() => {
                    setTagIds(
                      tagIds.includes(tag.id)
                        ? tagIds.filter((id) => id !== tag.id)
                        : [...tagIds, tag.id],
                    )
                    setDirty(true)
                  }}
                >
                  {tag.name}
                </Chip>
              ))}
            </div>
          )}
        </section>

        {errors.length > 0 && (
          <div className={styles['errors']} role="alert">
            {errors.map((error) => (
              <span key={error.code} className={styles['error']}>
                {error.message}
              </span>
            ))}
          </div>
        )}

        <div className={styles['formActions']}>
          <Button onClick={() => void navigate('/templates')}>Cancel</Button>
          <Button variant="primary" onClick={() => void save()}>
            {isNew ? 'Create check-in' : 'Save'}
          </Button>
        </div>
      </div>
    </>
  )
}

function Chevron({ up = false }: { readonly up?: boolean }) {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d={up ? 'M5 12l5-5 5 5' : 'M5 8l5 5 5-5'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Cross() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d="M5 5l10 10M15 5L5 15"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}
